import React from "react";
import { Button } from "@/components/ui/button";
import { 
  MapPin, 
  ArrowRight, 
  Phone, 
  User,
  CheckCircle,
  Navigation,
  Loader2,
  XCircle
} from "lucide-react";

const statusColors = {
  pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-blue-100 text-blue-800",
  picked_up: "bg-purple-100 text-purple-800",
  in_transit: "bg-indigo-100 text-indigo-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

/**
 * Card for partner's assigned deliveries with status actions
 */
export function AssignedDeliveryCard({ 
  delivery, 
  onUpdateStatus, 
  onDecline, 
  declining 
}) {
  const isActionable = delivery.status !== "delivered" && delivery.status !== "cancelled";
  const canDecline = delivery.status === "pending" || delivery.status === "confirmed";

  return (
    <div className="border border-border p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 font-bold text-lg mb-1">
            <MapPin className="w-4 h-4 text-green-600" />
            <span>{delivery.pickup_address}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <ArrowRight className="w-4 h-4" />
            <MapPin className="w-4 h-4 text-red-600" />
            <span>{delivery.drop_address}</span>
          </div>
        </div>
        <span className={`px-3 py-1 text-xs font-bold uppercase ${statusColors[delivery.status] || "bg-gray-100"}`}>
          {delivery.status?.replace("_", " ")}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-4 border-y border-border">
        <div>
          <p className="label-text mb-1">Package</p>
          <p className="font-medium capitalize">{delivery.package_type}</p>
        </div>
        <div>
          <p className="label-text mb-1">Weight</p>
          <p className="font-medium">{delivery.weight} kg</p>
        </div>
        <div>
          <p className="label-text mb-1">Payment</p>
          <p className="font-medium capitalize">{delivery.payment_method}</p>
        </div>
        <div>
          <p className="label-text mb-1">Amount</p>
          <p className="font-bold text-primary">₹{delivery.final_price}</p>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1">
            <Phone className="w-4 h-4 text-muted-foreground" />
            <span>{delivery.sender_phone}</span>
          </div>
          <div className="flex items-center gap-1">
            <User className="w-4 h-4 text-muted-foreground" />
            <span>{delivery.receiver_name}</span>
          </div>
        </div>

        {isActionable && (
          <div className="flex gap-2">
            {/* Decline button - available before pickup */}
            {canDecline && (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => onDecline(delivery.id)}
                disabled={declining === delivery.id}
                className="rounded-none gap-1"
                data-testid={`decline-delivery-${delivery.id}`}
              >
                {declining === delivery.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <XCircle className="w-4 h-4" />
                )}
                Decline
              </Button>
            )}
            
            <DeliveryStatusButton 
              delivery={delivery} 
              onUpdateStatus={onUpdateStatus} 
            />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Status action button that changes based on current delivery status
 */
function DeliveryStatusButton({ delivery, onUpdateStatus }) {
  const { status, id } = delivery;
  
  if (status === "pending") {
    return (
      <Button
        size="sm"
        onClick={() => onUpdateStatus(id, "confirmed")}
        className="rounded-none bg-blue-600 hover:bg-blue-700"
        data-testid={`confirm-${id}`}
      >
        <CheckCircle className="w-4 h-4 mr-1" />
        Confirm
      </Button>
    );
  }
  
  if (status === "confirmed") {
    return (
      <Button
        size="sm"
        onClick={() => onUpdateStatus(id, "picked_up")}
        className="rounded-none bg-purple-600 hover:bg-purple-700"
        data-testid={`pickup-${id}`}
      >
        <Navigation className="w-4 h-4 mr-1" />
        Mark Picked Up
      </Button>
    );
  }
  
  if (status === "picked_up") {
    return (
      <Button
        size="sm"
        onClick={() => onUpdateStatus(id, "in_transit")}
        className="rounded-none bg-indigo-600 hover:bg-indigo-700"
        data-testid={`transit-${id}`}
      >
        <Navigation className="w-4 h-4 mr-1" />
        Start Delivery
      </Button>
    );
  }
  
  if (status === "in_transit") {
    return (
      <Button
        size="sm"
        onClick={() => onUpdateStatus(id, "delivered")}
        className="rounded-none bg-green-600 hover:bg-green-700"
        data-testid={`deliver-${id}`}
      >
        <CheckCircle className="w-4 h-4 mr-1" />
        Mark Delivered
      </Button>
    );
  }
  
  return null;
}
